import { useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import { useAppSelector } from '@/redux/hooks'
import { selectThemeStyle } from '@/features/setting/setting.slice'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile'
import IdeyaTypography from '@/common/antd/ideya-typography'
import { ReloadControl } from '@/common/components/reload-control'

export interface SectionTitleBarRef {
  refreshTimestamp: () => void
}

interface SectionTitleBarProps {
  title: string
  ref?: React.Ref<SectionTitleBarRef>
  reload?: () => void
  action?: React.ReactNode
  isSerperate?: boolean
}

// React 19: ref is received as a regular prop, no forwardRef needed
export const SectionTitleBar = ({
  title,
  ref,
  reload,
  action,
  isSerperate = false,
}: SectionTitleBarProps) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const { lg } = useFixedBreakpoint()
  const [lastUpdatedTime, setLastUpdatedTime] = useState<Date>(new Date())

  useImperativeHandle(
    ref,
    () => ({
      refreshTimestamp: () => setLastUpdatedTime(new Date()),
    }),
    []
  )

  return (
    <Row
      justify={'space-between'}
      wrap={false}
      style={{
        height: '2.5rem',
        borderRadius: '.5rem',
        border: themeStyle.myTitleBorder,
        padding: '0.5rem 0.75rem',
        background: themeStyle.myTitleBg,
        ...(isSerperate
          ? { marginBottom: themeStyle.marginBase }
          : { borderRadius: '.5rem .5rem 0 0' }),
      }}
    >
      <Col>
        <IdeyaTypography.Text size={'base'}>{title}</IdeyaTypography.Text>
      </Col>
      <Col>
        <Row align={'middle'} style={{ gap: '0.625rem' }}>
          <Col>
            {!lg ? (
              <IdeyaTypography.Text size={'base'}>
                {lastUpdatedTime.toLocaleString('sv-SE', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false,
                })}
              </IdeyaTypography.Text>
            ) : (
              <IdeyaTypography.Text size={'base'}>
                {t('idea_list.latest_updated_time')} :{' '}
                {lastUpdatedTime.toLocaleString()}
              </IdeyaTypography.Text>
            )}
          </Col>
          {reload && (
            <Col>
              {/* The timestamp must reflect actual data arrival, so it advances only
                  via refreshTimestamp() from the grid's onRefreshed, never on click. */}
              {/* Matches the icon's 1s rotation so the throttle ends as the
                  animation does. */}
              <ReloadControl
                variant={'icon'}
                throttleMs={1000}
                onReload={reload}
              />
            </Col>
          )}
          {action}
        </Row>
      </Col>
    </Row>
  )
}
